import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    TextField,
    CircularProgress,
    Divider,
    ThemeProvider,
    createTheme,
} from "@mui/material";
import axios from "axios";
import BudgetAnalysisViewer from "../../component-files/viewers/BudgetAnalysisViewer";

// All axios calls will use this base
axios.defaults.baseURL = "https://figazuresql.swancreate.com/api/swa";

const lightTheme = createTheme({ palette: { mode: "light" } });

export default function BudgetAnalysisBySite() {
    const [schools, setSchools] = useState([]);
    const [schoolName, setSchoolName] = useState("ALL");
    const [customPrompt, setCustomPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const reportRef = useRef(null);

    // Load active schools
    useEffect(() => {
        axios
            .get("/SchoolMst", { params: { isActive: true } })
            .then(res => setSchools(res.data))
            .catch(err => console.error("Failed to load schools:", err));
    }, []);

    const handleGenerate = async () => {
        setLoading(true);
        setAnalysis(null);

        try {
            // Build params: include SchoolName only if not ALL
            const params = {};
            if (schoolName !== "ALL") {
                params.SchoolName = schoolName;
            }

            // 1) get the summary data
            const { data: raw } = await axios.get("/vw_SiteCategoryYearSummary", { params });

            // Determine target descriptor
            const targetDesc =
                schoolName === "ALL"
                    ? "all school sites in the district"
                    : `the cafeteria director of ${schoolName}`;

            // 2) build the AI prompt
            const prompt = `
You are a seasoned USDA & K–12 Nutrition Services finance advisor preparing a mid-year Budget Analysis report for ${targetDesc}. 
You have JSON containing Proposed Budget, YTD actuals, Prior Year totals, and 3-year projections for every category/subcategory.

— Write in an executive tone for both financial and non-financial audiences.
— Use 8–12 sentences per section, referencing exact dollar amounts and percentage variances.
— Highlight top over-performing and under-performing areas.
— End each section with a clear, actionable takeaway.
— Use these exact headers:

1) Executive Summary  
2) Participation Trends  
3) Revenue & Reimbursement Analysis  
4) Expenditure & Cost Management Strategies  
5) Areas of Excellence  
6) Areas for Improvement  
7) Risk Factors & Mitigation Recommendations  
8) Budget Adjustment Recommendations  

After section 8, include a bold **Summary of Recommended Budget Changes** paragraph listing specific increases (↑) or decreases (↓) to the current-year Proposed Budget by dollar amount and percentage.  
Additional Guidance: ${customPrompt || "None provided."}
      `.trim();

            // 3) call the AI
            const { data: ai } = await axios.post(
                "https://aiendpoint.swancreate.com/api/ai/gpt",
                { prompt, data: raw }
            );

            setAnalysis(ai.response);
        } catch (err) {
            console.error("Analysis error:", err);
            alert(`Analysis failed: ${err.response?.data || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        if (reportRef.current) window.print();
    };

    return (
        <ThemeProvider theme={lightTheme}>
            <Box sx={{ p: 4, bgcolor: "#fff", minHeight: "100vh", color: "#000" }}>
                <Typography variant="h4" mb={3}>
                    Budget Analysis by Site
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {/* Site selector */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Site</InputLabel>
                    <Select
                        value={schoolName}
                        label="Site"
                        onChange={e => setSchoolName(e.target.value)}
                    >
                        <MenuItem value="ALL">
                            <em>All Sites</em>
                        </MenuItem>
                        {schools.map(s => (
                            <MenuItem key={s.SchoolId} value={s.SchoolName}>
                                {s.SchoolName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Optional guidance */}
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Additional guidance (optional)"
                    sx={{ mb: 2 }}
                    value={customPrompt}
                    onChange={e => setCustomPrompt(e.target.value)}
                />

                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleGenerate}
                    disabled={loading}
                    sx={{ mb: 3 }}
                >
                    {loading ? <CircularProgress size={24} /> : "Generate Analysis"}
                </Button>

                {analysis && (
                    <>
                        <Button variant="outlined" onClick={handlePrint} sx={{ mb: 2 }}>
                            Print Report
                        </Button>

                        <Box
                            ref={reportRef}
                            sx={{ p: 3, bgcolor: "#f9f9f9", borderRadius: 2, boxShadow: 1 }}
                        >
                            <BudgetAnalysisViewer response={analysis} />
                        </Box>
                    </>
                )}
            </Box>
        </ThemeProvider>
    );
}
